/**
 * Connectivity model — the bridge between *what is configured* (FeatureFlags,
 * derived from config) and *what is usable right now* (given online/offline).
 *
 * Life OS follows a "local-first core, cloud-enhanced everything else, with
 * graceful degradation" model. Every online capability falls into a tier:
 *
 *  - `offline-capable`  the local app itself: capture, tasks, today, notes,
 *                       search, review… always works, network or not. These are
 *                       NOT modelled here because they never depend on config.
 *  - `online-enhanced`  works offline by queueing locally, then reconciles when
 *                       a connection returns (cloud sync, cloud backup upload).
 *  - `online-only`      genuinely needs the network at the moment of use
 *                       (AI calls, email/Telegram/WhatsApp/voice delivery, push).
 *
 * Everything here is PURE so the UI can honestly show, per capability, whether
 * it is available, degraded, unavailable while offline, or simply unconfigured.
 */

import type { FeatureFlags } from './config.js';

export type ConnectivityState = 'online' | 'offline';

export type FeatureTier = 'offline-capable' | 'online-enhanced' | 'online-only';

export type CapabilityStatus =
  /** Configured and fully usable right now. */
  | 'available'
  /** Configured; works locally now and reconciles with the cloud once online. */
  | 'degraded'
  /** Configured but needs the network, which is currently unavailable. */
  | 'unavailable-offline'
  /** No credentials/flags supplied; the feature is simply turned off. */
  | 'not-configured';

export type CapabilityId = keyof FeatureFlags;

export interface CapabilityAvailability {
  readonly id: CapabilityId;
  readonly tier: FeatureTier;
  readonly configured: boolean;
  readonly status: CapabilityStatus;
}

/** Tier assignment for every online capability tracked by FeatureFlags. */
export const CAPABILITY_TIERS: Readonly<Record<CapabilityId, FeatureTier>> = {
  cloudSync: 'online-enhanced',
  cloudBackup: 'online-enhanced',
  ai: 'online-only',
  email: 'online-only',
  telegram: 'online-only',
  whatsapp: 'online-only',
  voiceCalls: 'online-only',
  webPush: 'online-only',
};

const CAPABILITY_IDS = Object.keys(CAPABILITY_TIERS) as CapabilityId[];

function statusFor(
  tier: FeatureTier,
  configured: boolean,
  state: ConnectivityState,
): CapabilityStatus {
  if (!configured) return 'not-configured';
  if (state === 'online') return 'available';
  // Offline:
  if (tier === 'online-enhanced') return 'degraded';
  return 'unavailable-offline';
}

/** Availability of every online capability given config + current connectivity. Pure. */
export function capabilityAvailability(
  flags: FeatureFlags,
  state: ConnectivityState,
): readonly CapabilityAvailability[] {
  return CAPABILITY_IDS.map((id) => {
    const tier = CAPABILITY_TIERS[id];
    const configured = flags[id];
    return { id, tier, configured, status: statusFor(tier, configured, state) };
  });
}

/** Capabilities the owner can actually act on right now (available or degraded). Pure. */
export function usableCapabilities(
  flags: FeatureFlags,
  state: ConnectivityState,
): readonly CapabilityId[] {
  return capabilityAvailability(flags, state)
    .filter((c) => c.status === 'available' || c.status === 'degraded')
    .map((c) => c.id);
}

export interface ConnectivitySummary {
  readonly state: ConnectivityState;
  readonly configured: number;
  readonly available: number;
  readonly degraded: number;
  readonly unavailableOffline: number;
  readonly notConfigured: number;
}

/** Roll-up counts for the Runtime Status / local-first footer. Pure. */
export function connectivitySummary(
  flags: FeatureFlags,
  state: ConnectivityState,
): ConnectivitySummary {
  const caps = capabilityAvailability(flags, state);
  return {
    state,
    configured: caps.filter((c) => c.configured).length,
    available: caps.filter((c) => c.status === 'available').length,
    degraded: caps.filter((c) => c.status === 'degraded').length,
    unavailableOffline: caps.filter((c) => c.status === 'unavailable-offline').length,
    notConfigured: caps.filter((c) => c.status === 'not-configured').length,
  };
}

/** Human-readable, secret-free reason for a capability's current status. Pure. */
export function capabilityReason(c: CapabilityAvailability): string {
  switch (c.status) {
    case 'available':
      return 'Ready.';
    case 'degraded':
      return 'Working locally; will sync when back online.';
    case 'unavailable-offline':
      return 'Needs internet — unavailable while offline.';
    case 'not-configured':
      return 'Not configured.';
  }
}
